import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { message, Button, theme, Result } from 'antd';
import { SyncOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

import OrdersFilter from './components/OrdersFilter';
import OrdersList from './components/OrdersList';
import SyncOrdersModal from './components/SyncOrdersModal';
import KpiDashboard from './components/KpiDashboard';
import useUrlSync from './hooks/useUrlSync';
import { FILTER_TYPES, isValidFilterType } from './constants/filterTypes';
import { orderPermissions, ORDER_PERMISSIONS, hasPermission } from '../../utils/permissions';
import {
    fetchOrderItems,
    fetchKpiMetrics,
    handleSearchChange,
    handleStatusChange,
    handleDateRangeChange,
    handleSellerAccountChange,
    handleCustomizationStatusChange,
    handlePrinterChange,
    handleFulfillmentStatusChange,
    handleFulfillmentChannelChange,
    handleShipmentStatusChange,
    handleShipmentDetailChange,
    handleSupporterChange,
    handleCategoryChange,
    handleMessageStatusChange,
    handleShipDateDueChange,
    handleDeliveryDateDueChange,
    handleConfirmShipmentChange,
    handleUpdatedLastMileChange,
    handleMockupSentAtChange,
    handleTitleChange,
    handleSellerSkuChange,
    handleEmployeeIdChange,
    handleExcludeTitleChange,
    handleExcludeSellerSkuChange,
    handleBasecostChange,
    handleQuantityChange,
    handleClearFilters,
    applyFilters,
    handlePaginationChange,
    syncAmazonOrders
} from './actions';
import { setFilters, setPagination } from './reducers';

const Orders = ({
    // Props từ mapStateToProps
    orderItems,
    filteredOrderItems,
    loading,
    pagination,
    filters,
    user, // Thêm user từ auth state
    // Props từ mapDispatchToProps (action creators đã được bind)
    fetchOrderItems,
    fetchKpiMetrics,
    handleSearchChange,
    handleStatusChange,
    handleDateRangeChange,
    handleSellerAccountChange,
    handleCustomizationStatusChange,
    handlePrinterChange,
    handleFulfillmentStatusChange,
    handleFulfillmentChannelChange,
    handleShipmentStatusChange,
    handleShipmentDetailChange,
    handleSupporterChange,
    handleCategoryChange,
    handleMessageStatusChange,
    handleShipDateDueChange,
    handleDeliveryDateDueChange,
    handleConfirmShipmentChange,
    handleUpdatedLastMileChange,
    handleMockupSentAtChange,
    handleTitleChange,
    handleSellerSkuChange,
    handleEmployeeIdChange,
    handleExcludeTitleChange,
    handleExcludeSellerSkuChange,
    handleBasecostChange,
    handleQuantityChange,
    handleClearFilters,
    applyFilters,
    handlePaginationChange,
    syncAmazonOrders,
    setFilters,
    setPagination
}) => {
    const navigate = useNavigate();
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    const { searchText, selectedStatus, selectedDateRange, selectedSellerAccount, selectedCustomizationStatus, selectedPrinter, selectedFulfillmentStatus, selectedFulfillmentChannel, selectedShipmentStatus, selectedSupporter, selectedCategory, selectedMessageStatus, selectedShipDateDue, selectedDeliveryDateDue, selectedConfirmShipment, selectedUpdatedLastMile, selectedMockupSentAt, selectedTitle, selectedSellerSku, selectedEmployeeId, selectedExcludeTitle, selectedExcludeSellerSku, selectedBasecost } = filters;

    const [syncModalVisible, setSyncModalVisible] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [clearSelectedRowsSignal, setClearSelectedRowsSignal] = useState(0);

    // Compute locked employee ID - non-admin users with employeeCode are auto-filtered
    const isAdminRole = user?.userRole?.name === 'Super Admin' || user?.userRole?.name === 'Admin';
    const lockedEmployeeId = (!isAdminRole && user?.employeeCode) ? user.employeeCode : null;

    // URL synchronization handlers
    const handleFiltersChange = useCallback((newFilters) => {
        setFilters(newFilters);
    }, [setFilters]);

    const handlePaginationUpdate = useCallback((newPagination) => {
        setPagination(newPagination);
    }, [setPagination]);

    // Initialize URL sync hook
    const { updateUrl } = useUrlSync(filters, pagination, handleFiltersChange, handlePaginationUpdate);

    // Initialize page on mount or when URL params change
    useEffect(() => {
        if (!isInitialized) {
            setIsInitialized(true);
        }
    }, [isInitialized]);

    // useEffect này chỉ fetchOrderItems khi đã isInitialized
    // useEffect(() => {
    //     if (isInitialized) {
    //         fetchOrderItems(pagination.current, pagination.pageSize, updateUrl);
    //     }
    // }, [fetchOrderItems, pagination.current, pagination.pageSize, updateUrl, isInitialized, filters]);

    useEffect(() => {
        if (isInitialized) {
            const loadInitialOrders = async () => {
                // Pre-set employee filter for non-admin users with employeeCode
                if (lockedEmployeeId) {
                    handleEmployeeIdChange(lockedEmployeeId);
                }
                await fetchOrderItems(pagination.current, pagination.pageSize, updateUrl);
                fetchKpiMetrics();
            };

            loadInitialOrders();
        }
    }, [isInitialized]);

    // Generic filter handler - optimized approach
    const filterHandlers = useMemo(() => ({
        [FILTER_TYPES.SEARCH]: handleSearchChange,
        [FILTER_TYPES.STATUS]: handleStatusChange,
        [FILTER_TYPES.DATE_RANGE]: handleDateRangeChange,
        [FILTER_TYPES.SELLER_ACCOUNT]: handleSellerAccountChange,
        [FILTER_TYPES.CUSTOMIZATION_STATUS]: handleCustomizationStatusChange,
        [FILTER_TYPES.PRINTER]: handlePrinterChange,
        [FILTER_TYPES.FULFILLMENT_STATUS]: handleFulfillmentStatusChange,
        [FILTER_TYPES.FULFILLMENT_CHANNEL]: handleFulfillmentChannelChange,
        [FILTER_TYPES.SHIPMENT_STATUS]: handleShipmentStatusChange,
        [FILTER_TYPES.SHIPMENT_DETAIL]: handleShipmentDetailChange,
        [FILTER_TYPES.SUPPORTER]: handleSupporterChange,
        [FILTER_TYPES.CATEGORY]: handleCategoryChange,
        [FILTER_TYPES.MESSAGE_STATUS]: handleMessageStatusChange,
        [FILTER_TYPES.SHIP_DATE_DUE]: handleShipDateDueChange,
        [FILTER_TYPES.DELIVERY_DATE_DUE]: handleDeliveryDateDueChange,
        [FILTER_TYPES.CONFIRM_SHIPMENT]: handleConfirmShipmentChange,
        [FILTER_TYPES.UPDATED_LAST_MILE]: handleUpdatedLastMileChange,
        [FILTER_TYPES.MOCKUP_SENT_AT]: handleMockupSentAtChange,
        [FILTER_TYPES.TITLE]: handleTitleChange,
        [FILTER_TYPES.SELLER_SKU]: handleSellerSkuChange,
        [FILTER_TYPES.EMPLOYEE_ID]: handleEmployeeIdChange,
        [FILTER_TYPES.EXCLUDE_TITLE]: handleExcludeTitleChange,
        [FILTER_TYPES.EXCLUDE_SELLER_SKU]: handleExcludeSellerSkuChange,
        [FILTER_TYPES.BASECOST]: handleBasecostChange,
        [FILTER_TYPES.QUANTITY]: handleQuantityChange
    }), [
        handleSearchChange,
        handleStatusChange,
        handleDateRangeChange,
        handleSellerAccountChange,
        handleCustomizationStatusChange,
        handlePrinterChange,
        handleFulfillmentStatusChange,
        handleFulfillmentChannelChange,
        handleShipmentStatusChange,
        handleShipmentDetailChange,
        handleSupporterChange,
        handleCategoryChange,
        handleMessageStatusChange,
        handleShipDateDueChange,
        handleDeliveryDateDueChange,
        handleConfirmShipmentChange,
        handleUpdatedLastMileChange,
        handleMockupSentAtChange,
        handleTitleChange,
        handleSellerSkuChange,
        handleEmployeeIdChange,
        handleExcludeTitleChange,
        handleExcludeSellerSkuChange,
        handleBasecostChange,
        handleQuantityChange
    ]);

    // Generic filter change handler with validation
    const handleFilterChange = useCallback((filterType, value) => {
        if (!isValidFilterType(filterType)) {
            console.warn(`Invalid filter type: ${filterType}. Available types:`, Object.values(FILTER_TYPES));
            return;
        }

        const handler = filterHandlers[filterType];
        if (handler) {
            handler(value);
        } else {
            console.warn(`No handler found for filter type: ${filterType}`);
        }
    }, [filterHandlers]);

    const handleClearAllFilters = useCallback(() => {
        handleClearFilters(updateUrl);
        // Restore locked employee filter after clear
        if (lockedEmployeeId) {
            setTimeout(() => {
                handleEmployeeIdChange(lockedEmployeeId);
            }, 0);
        }
        // Clear selected rows when filters are cleared
        setClearSelectedRowsSignal(prev => prev + 1);
    }, [handleClearFilters, updateUrl, lockedEmployeeId, handleEmployeeIdChange]);

    const handleApplyFilters = useCallback(() => {
        applyFilters(updateUrl);
        // Clear selected rows when filters are applied
        setClearSelectedRowsSignal(prev => prev + 1);
    }, [applyFilters, updateUrl]);

    const handleReload = useCallback(async (options = {}) => {
        // Reload with current filters and preserve pagination
        await fetchOrderItems(pagination.current, pagination.pageSize, updateUrl);
        fetchKpiMetrics(); // Also refresh KPI data after the table is ready
        // Only clear selected rows if not preserving selection
        if (!options.preserveSelection) {
            setClearSelectedRowsSignal(prev => prev + 1);
        }
    }, [fetchOrderItems, fetchKpiMetrics, pagination.current, pagination.pageSize, updateUrl]);

    const handleTablePaginationChange = useCallback((page, pageSize) => {
        handlePaginationChange(page, pageSize, updateUrl);
        // Clear selected rows when pagination changes
        setClearSelectedRowsSignal(prev => prev + 1);
    }, [handlePaginationChange, updateUrl]);

    // Memoize style objects to prevent re-creation
    const kpiDashboardStyle = useMemo(() => ({
        margin: '10px',
        borderRadius: borderRadiusLG,
    }), [borderRadiusLG]);

    const mainContainerStyle = useMemo(() => ({
        margin: '0px 10px 10px 10px',
        padding: '5px 16px 16px 16px',
        background: colorBgContainer,
        borderRadius: borderRadiusLG,
        minHeight: 280,
    }), [colorBgContainer, borderRadiusLG]);

    // Check permissions
    const canViewOrders = orderPermissions.canView(user);
    const canViewKpiDashboard = orderPermissions.canViewKpiDashboard(user);
    const canSyncAmazonOrders = orderPermissions.canSyncAmazon(user);

    // If user doesn't have permission to view orders, show access denied
    if (!canViewOrders) {
        return (
            <Result
                status="403"
                title="403"
                subTitle="Bạn không có quyền truy cập vào trang này."
                extra={
                    <Button type="primary" onClick={() => navigate('/')}>
                        Quay về trang chủ
                    </Button>
                }
            />
        );
    }

    const handleSyncClick = () => {
        // Check permission before allowing sync
        if (!canSyncAmazonOrders) {
            message.warning('Bạn không có quyền đồng bộ đơn hàng từ Amazon');
            return;
        }
        setSyncModalVisible(true);
    };

    const handleSyncOk = async (selectedSellerAccountIds, type, dateRange, amazonOrderIds) => {
        try {
            setSyncLoading(true);

            if (!selectedSellerAccountIds || selectedSellerAccountIds.length === 0) {
                message.error('Vui lòng chọn ít nhất một Seller Account');
                return;
            }

            const syncParams = {
                sellerAccountIds: selectedSellerAccountIds,
                type
            };

            // Thêm Amazon Order IDs nếu có
            if (amazonOrderIds && amazonOrderIds.length > 0) {
                syncParams.amazonOrderIds = amazonOrderIds;
            } else if (dateRange) {
                // Chỉ thêm dateRange nếu không có Amazon Order IDs
                syncParams.dateRange = dateRange;
            }

            const result = await syncAmazonOrders(syncParams);

            if (result.success) {
                message.success(result.message);
                setSyncModalVisible(false);
            } else {
                message.error(result.message);
            }
        } catch (error) {
            console.error('Error syncing Amazon orders:', error);
            message.error('Có lỗi xảy ra khi đồng bộ đơn hàng từ Amazon');
        } finally {
            setSyncLoading(false);
        }
    };

    const handleSyncCancel = () => {
        setSyncModalVisible(false);
    };

    return (
        <div>
            {/* KPI Dashboard Section - Only show if user has permission */}
            {canViewKpiDashboard && (
                <div style={kpiDashboardStyle}>
                    <KpiDashboard onFilterChange={handleFilterChange} onApplyFilters={handleApplyFilters} />
                </div>
            )}
            <div
                style={{
                    margin: '0px 10px 0px 10px',
                }}
            >
                <OrdersFilter
                    onFilterChange={handleFilterChange}
                    onClearFilters={handleClearAllFilters}
                    onApplyFilters={handleApplyFilters}
                    onReload={handleReload}
                    filters={filters}
                    loading={loading}
                    lockedEmployeeId={lockedEmployeeId}
                />
            </div>
            <div style={mainContainerStyle}>

                <OrdersList
                    orderItems={filteredOrderItems}
                    isLoading={loading}
                    pagination={pagination}
                    onSyncClick={handleSyncClick}
                    onPaginationChange={handleTablePaginationChange}
                    clearSelectedRowsSignal={clearSelectedRowsSignal}
                    user={user} // Pass user to OrdersList for permission checks
                    canSyncAmazonOrders={canSyncAmazonOrders}
                />
            </div>
            {/* Sync Modal - Only show if user has permission */}
            {canSyncAmazonOrders && (
                <SyncOrdersModal
                    visible={syncModalVisible}
                    onOk={handleSyncOk}
                    onCancel={handleSyncCancel}
                    loading={syncLoading}
                />
            )}
        </div>
    );
};

// mapStateToProps - Chỉ lấy những state cần thiết
const mapStateToProps = (state) => {
    const ordersState = state.orders;
    const authState = state.auth;
    return {
        orderItems: ordersState.orderItems,
        filteredOrderItems: ordersState.filteredOrderItems,
        loading: ordersState.loading,
        pagination: ordersState.pagination,
        filters: ordersState.filters,
        user: authState.user, // Thêm user từ auth state
    };
};

// mapDispatchToProps - Sử dụng object notation để tự động bind actions
const mapDispatchToProps = {
    fetchOrderItems,
    fetchKpiMetrics,
    handleSearchChange,
    handleStatusChange,
    handleDateRangeChange,
    handleSellerAccountChange,
    handleCustomizationStatusChange,
    handlePrinterChange,
    handleFulfillmentStatusChange,
    handleFulfillmentChannelChange,
    handleShipmentStatusChange,
    handleShipmentDetailChange,
    handleSupporterChange,
    handleCategoryChange,
    handleMessageStatusChange,
    handleShipDateDueChange,
    handleDeliveryDateDueChange,
    handleConfirmShipmentChange,
    handleUpdatedLastMileChange,
    handleMockupSentAtChange,
    handleTitleChange,
    handleSellerSkuChange,
    handleEmployeeIdChange,
    handleExcludeTitleChange,
    handleExcludeSellerSkuChange,
    handleBasecostChange,
    handleQuantityChange,
    handleClearFilters,
    applyFilters,
    handlePaginationChange,
    syncAmazonOrders,
    setFilters,
    setPagination
};

export default connect(mapStateToProps, mapDispatchToProps)(Orders);
